import React, { useEffect, useState } from "react";

import SearchIcon from "@assets/icons/search.svg";
import CrossIcon from "@assets/icons/search-cross.svg";

import { SearchInput } from "@app/components/search-input/search-input";
import { IconComponent } from "@app/core";
import { useDebouncedValue } from "@app/util/hooks/use-debounced-value";
import { authThunks } from "@app/redux/thunks/auth.thunk";
import { useOnClickOutside } from "@app/util/hooks/use-on-click-outside";
import { DealCardAddAlert } from "@app/components/deal-alert-cards/deal-card-add-alert/deal-card-add-alert";
import { useAppSelector, useAppDispatch } from "@app/redux/store";
import { authReducer } from "@app/redux/reducers/auth";
import { formatMessage } from "@app/translations/intl";
import { DataWarehouseProductApi, DataWarehouseProductDTO, OutDealDTO } from "@app/bf-api";

import styles from "./deal-alert-search.module.scss";

export interface IDealAlertSearchProps {
  searchPlaceholder?: string;
  onDropdownToggle?: (isOpen: boolean) => void;
}

const DealAlertSearch = ({ searchPlaceholder, onDropdownToggle }: IDealAlertSearchProps) => {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [searchValue, setSearchValue] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [debouncedSearchValue] = useDebouncedValue(searchValue, 1000);
  const [searchResults, setSearchResults] = useState<DataWarehouseProductDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const searchResultsRef = React.useRef<HTMLDivElement>(null);
  useOnClickOutside(searchResultsRef, () => handleDropdownToggle(false));

  const handleDropdownToggle = (isOpen: boolean) => {
    setIsDropdownOpen(isOpen);
    onDropdownToggle?.(isOpen);
  };

  useEffect(() => {
    if (debouncedSearchValue) {
      search(debouncedSearchValue);
      handleDropdownToggle(true);
    } else {
      setSearchResults([]);
      handleDropdownToggle(false);
    }
  }, [debouncedSearchValue]);

  const handleAddDeal = (deal: DataWarehouseProductDTO) => {
    if (user) {
      dispatch(authThunks.addLikedDeal(deal.ean, true));
      return;
    }

    dispatch(authReducer.setEanToLike(deal.ean));
    setIsDropdownOpen(false);
  };

  const search = async (q: string) => {
    setIsLoading(true);
    const api = new DataWarehouseProductApi();
    const response = await api.getDataWarehouseProductData({
      searchQuery: q
    });
    setSearchResults(response || []);
    setIsLoading(false);
  };

  return (
    <div ref={searchResultsRef} className={styles.search}>
      <SearchInput
        id="searchInputSearchPage"
        className={styles.searchInput}
        icon={<IconComponent fillColor="white" size="14px" icon={SearchIcon} />}
        iconPosition="right"
        onFocus={() => searchValue && handleDropdownToggle(true)}
        onChange={(e) => setSearchValue(e.target.value)}
        placeholder={searchPlaceholder}
        type="text"
        value={searchValue || ""}
        deleteIcon={<IconComponent fillColor="white" size="14px" icon={CrossIcon} />}
        onDelete={() => setSearchValue("")}
      />

      {isDropdownOpen && (
        <div className={styles.searchResults}>
          {searchResults.length > 0
            ? searchResults.map((deal) => (
                <DealCardAddAlert key={deal.id} deal={deal} className={styles.card} onClick={handleAddDeal} />
              ))
            : searchValue && (
                <div className={styles.noResults}>
                  {formatMessage({ id: isLoading ? "myDealAlerts.search.loading" : "myDealAlerts.search.noResults" })}
                </div>
              )}
        </div>
      )}
    </div>
  );
};

export { DealAlertSearch };
